import { MobileManager } from '../core/MobileManager';

/**
 * TouchFireZone - Touch area for firing on mobile
 * Right half of screen is the fire zone
 * Tap = single shot, Hold = continuous fire
 * Double tap = weapon switch
 */
export class TouchFireZone {
  private container: HTMLElement;
  private element: HTMLElement;
  private feedbackElement: HTMLElement;

  private isFiring: boolean = false;
  private touchId: number | null = null;
  private lastTapTime: number = 0;
  private weaponSwitchCallback: (() => void) | null = null;

  private readonly DOUBLE_TAP_THRESHOLD = 300; // ms

  constructor(container: HTMLElement) {
    this.container = container;

    this.element = this.createFireZoneElement();
    this.feedbackElement = this.element.querySelector('.fire-feedback') as HTMLElement;

    this.container.appendChild(this.element);

    this.bindEvents();
    this.updateVisibility();

    window.addEventListener('resize', this.updateVisibility);
  }

  private createFireZoneElement(): HTMLElement {
    const zone = document.createElement('div');
    zone.className = 'touch-fire-zone';
    zone.innerHTML = `
      <div class="fire-feedback"></div>
      <div class="fire-hint">TAP TO FIRE</div>
    `;

    const style = document.createElement('style');
    style.id = 'touch-fire-zone-styles';

    if (!document.getElementById('touch-fire-zone-styles')) {
      style.textContent = `
        .touch-fire-zone {
          position: fixed;
          top: 0;
          right: 0;
          width: 50%;
          height: 100%;
          z-index: 900;
          touch-action: none;
          user-select: none;
          -webkit-user-select: none;
          display: none;
        }

        .touch-fire-zone.visible {
          display: block;
        }

        .fire-feedback {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100px;
          height: 100px;
          margin-left: -50px;
          margin-top: -50px;
          border-radius: 50%;
          background: radial-gradient(circle,
            rgba(255, 100, 100, 0.6) 0%,
            rgba(255, 50, 50, 0.3) 50%,
            transparent 70%);
          opacity: 0;
          transform: scale(0.5);
          transition: opacity 0.1s, transform 0.1s;
          pointer-events: none;
        }

        .fire-feedback.active {
          opacity: 1;
          transform: scale(1);
        }

        .fire-hint {
          position: absolute;
          bottom: 100px;
          right: 30px;
          color: rgba(255, 255, 255, 0.3);
          font-family: 'Orbitron', 'Segoe UI', sans-serif;
          font-size: 12px;
          letter-spacing: 2px;
          pointer-events: none;
          opacity: 0.5;
        }

        /* Visual boundary indicator */
        .touch-fire-zone::before {
          content: '';
          position: absolute;
          left: 0;
          top: 10%;
          height: 80%;
          width: 2px;
          background: linear-gradient(
            to bottom,
            transparent 0%,
            rgba(255, 100, 100, 0.2) 20%,
            rgba(255, 100, 100, 0.3) 50%,
            rgba(255, 100, 100, 0.2) 80%,
            transparent 100%
          );
        }

        /* Crosshair in center when firing */
        .touch-fire-zone.firing .fire-feedback::before,
        .touch-fire-zone.firing .fire-feedback::after {
          content: '';
          position: absolute;
          background: rgba(255, 100, 100, 0.8);
        }

        .touch-fire-zone.firing .fire-feedback::before {
          width: 2px;
          height: 20px;
          left: 50%;
          top: 50%;
          margin-left: -1px;
          margin-top: -10px;
        }

        .touch-fire-zone.firing .fire-feedback::after {
          width: 20px;
          height: 2px;
          left: 50%;
          top: 50%;
          margin-left: -10px;
          margin-top: -1px;
        }
      `;
      document.head.appendChild(style);
    }

    return zone;
  }

  private bindEvents(): void {
    this.element.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    document.addEventListener('touchend', this.handleTouchEnd);
    document.addEventListener('touchcancel', this.handleTouchEnd);
  }

  private handleTouchStart = (e: TouchEvent): void => {
    e.preventDefault();

    // Check for double tap (weapon switch)
    const now = performance.now();
    if (now - this.lastTapTime < this.DOUBLE_TAP_THRESHOLD) {
      if (this.weaponSwitchCallback) {
        this.weaponSwitchCallback();
      }
      this.lastTapTime = 0; // Reset to prevent triple-tap
      return;
    }
    this.lastTapTime = now;

    if (this.touchId !== null) return; // Already tracking

    const touch = e.changedTouches[0];
    this.touchId = touch.identifier;
    this.isFiring = true;

    // Show feedback at touch position
    this.showFeedback(touch.clientX, touch.clientY);
    this.element.classList.add('firing');
  };

  private handleTouchEnd = (e: TouchEvent): void => {
    if (this.touchId === null) return;

    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === this.touchId) {
        this.touchId = null;
        this.isFiring = false;
        this.hideFeedback();
        this.element.classList.remove('firing');
        break;
      }
    }
  };

  private showFeedback(x: number, y: number): void {
    const rect = this.element.getBoundingClientRect();
    const localX = x - rect.left;
    const localY = y - rect.top;

    this.feedbackElement.style.left = `${localX}px`;
    this.feedbackElement.style.top = `${localY}px`;
    this.feedbackElement.style.marginLeft = '-50px';
    this.feedbackElement.style.marginTop = '-50px';
    this.feedbackElement.classList.add('active');
  }

  private hideFeedback(): void {
    this.feedbackElement.classList.remove('active');
  }

  private updateVisibility = (): void => {
    if (MobileManager.isTouchDevice()) {
      this.element.classList.add('visible');
    } else {
      this.element.classList.remove('visible');
    }
  };

  /**
   * Check if player is currently firing
   */
  public getIsFiring(): boolean {
    return this.isFiring;
  }

  /**
   * Set callback for weapon switch (double tap)
   */
  public onWeaponSwitch(callback: () => void): void {
    this.weaponSwitchCallback = callback;
  }

  /**
   * Force show/hide the fire zone
   */
  public setVisible(visible: boolean): void {
    if (visible) {
      this.element.classList.add('visible');
    } else {
      this.element.classList.remove('visible');
    }
  }

  /**
   * Cleanup and remove the fire zone
   */
  public destroy(): void {
    this.element.removeEventListener('touchstart', this.handleTouchStart);
    document.removeEventListener('touchend', this.handleTouchEnd);
    document.removeEventListener('touchcancel', this.handleTouchEnd);
    window.removeEventListener('resize', this.updateVisibility);

    this.element.remove();
  }
}
